/**
 * TrustGate Guided Interaction Layer - Industry Flow Presets
 * BOUNDARY: Semantic presets only, no logic forks
 * 
 * Based on Intent Model: Cross-Industry Verification Framework
 * Each flow provides rich educational context and industry-specific terminology
 */

import type { FlowPreset } from './types';

const legalEvidenceFilingFlow: FlowPreset = {
  id: 'legal_evidence_filing',
  name: 'Legal Evidence & Compliance Filing',
  description: 'Submit evidence filing or compliance attestation requiring review and approval. Generates immutable proof suitable for exhibits and chain of custody.',
  industry: 'legal',
  category: 'compliance',
  requiresAuthority: true,
  auditClass: 'legal_filing',
  steps: [
    {
      id: 'filing_type',
      label: 'Filing Category',
      description: '📋 What type of legal action or compliance check is this?\n\nExamples:\n• Evidence submission (document filing)\n• Compliance attestation (regulatory check)\n• Audit trail verification (chain of custody)\n• Access control validation (authorization proof)',
      fieldName: 'filingType',
      fieldType: 'select',
      options: [
        'evidence_submission',
        'compliance_attestation', 
        'audit_trail_verification',
        'access_control_validation',
        'regulatory_filing'
      ],
      required: true,
      validation: (value) => value ? null : 'Filing category is required'
    },
    {
      id: 'case_reference',
      label: 'Case/Matter Reference',
      description: '🔖 Docket number, matter ID, or regulatory reference\n\nFormat: [Jurisdiction]-[Year]-[Number] or internal matter ID\nExamples: "CA-2026-12345", "SEC-2026-COMP-4567", "MATTER-2026-001"',
      fieldName: 'caseReference',
      fieldType: 'text',
      required: true,
      placeholder: 'CA-2026-12345 or MATTER-2026-001',
      validation: (value) => {
        if (!value?.trim()) return 'Case reference is required';
        if (value.length < 5) return 'Case reference must be at least 5 characters';
        if (value.length > 100) return 'Max 100 characters';
        return null;
      }
    },
    {
      id: 'document_hash',
      label: 'Document Hash (Optional)',
      description: '🔐 SHA-256 hash of the document being filed\n\nIf you are attaching external evidence, provide its cryptographic hash here for chain of custody. TrustGate will include this in the immutable proof.',
      fieldName: 'documentHash',
      fieldType: 'text',
      required: false,
      placeholder: 'a3f5e8d2c1b4...',
      validation: (value) => {
        if (!value) return null;
        if (!/^[a-fA-F0-9]{64}$/.test(value)) return 'Must be 64-character hex SHA-256 hash';
        return null;
      }
    },
    {
      id: 'filing_memo',
      label: 'Filing Memorandum',
      description: '📝 Detailed explanation of this filing\n\n**Include:**\n• Purpose of filing\n• Precedent or regulatory basis\n• Expected outcome (admissible, compliant, etc.)\n• Chain of custody notes (if evidence)\n\n**Minimum 50 characters for legal record.**',
      fieldName: 'filingMemo',
      fieldType: 'textarea',
      required: true,
      validation: (value) => {
        if (!value?.trim()) return 'Filing memorandum is required';
        if (value.length < 50) return 'Minimum 50 characters required for legal record';
        if (value.length > 5000) return 'Max 5000 characters';
        return null;
      }
    },
    {
      id: 'expected_verdict',
      label: 'Expected Verdict',
      description: '⚖️ What outcome are you expecting?\n\n• **Compliant/Admissible**: Positive test (should succeed)\n• **Non-compliant/Inadmissible**: Negative test (should be rejected)\n• **Baseline match**: Regression check (unchanged from precedent)',
      fieldName: 'expectedVerdict',
      fieldType: 'select',
      options: ['compliant', 'non_compliant', 'baseline_match'],
      required: true,
      validation: (value) => value ? null : 'Expected verdict is required'
    }
  ]
};

const financeControlAttestationFlow: FlowPreset = {
  id: 'finance_control_attestation',
  name: 'Financial Control & Transaction Validation',
  description: 'Submit financial control test, transaction validation, or SOX attestation. Produces audit evidence with point-in-time attestation.',
  industry: 'finance',
  category: 'controls',
  requiresAuthority: true,
  auditClass: 'financial_control',
  steps: [
    {
      id: 'control_type',
      label: 'Control Test Type',
      description: '💰 What type of financial control are you testing?\n\nExamples:\n• **Threshold check**: Amount within approval limits\n• **Segregation of duties**: Authorization properly separated\n• **Reconciliation**: Balances match source systems\n• **Compliance limit**: Transaction within regulatory bounds',
      fieldName: 'controlType',
      fieldType: 'select',
      options: [
        'threshold_check',
        'segregation_of_duties',
        'reconciliation',
        'regulatory_compliance',
        'approval_workflow'
      ],
      required: true,
      validation: (value) => value ? null : 'Control type is required'
    },
    {
      id: 'transaction_amount',
      label: 'Transaction Amount (USD)',
      description: '💵 Dollar amount being tested (in USD)\n\nProvide the exact amount subject to control testing.\nFormat: Dollars and cents (e.g., 50000.00 for $50k)',
      fieldName: 'amount',
      fieldType: 'text',
      required: true,
      placeholder: '50000.00',
      validation: (value) => {
        if (!value?.trim()) return 'Amount is required';
        const num = parseFloat(value);
        if (isNaN(num)) return 'Must be a valid number';
        if (num < 0) return 'Amount cannot be negative';
        if (num > 100000000) return 'Amount exceeds maximum ($100M)';
        return null;
      }
    },
    {
      id: 'policy_threshold',
      label: 'Policy Threshold / Baseline',
      description: '📊 What is the expected threshold or baseline?\n\nExamples:\n• "Requires VP approval above $25,000"\n• "Must match source balance within 0.01%"\n• "Segregation required for amounts > $10,000"\n\n**This defines your control expectation.**',
      fieldName: 'policyThreshold',
      fieldType: 'textarea',
      required: true,
      validation: (value) => {
        if (!value?.trim()) return 'Policy threshold is required';
        if (value.length < 10) return 'Min 10 characters';
        if (value.length > 500) return 'Max 500 characters';
        return null;
      }
    },
    {
      id: 'destination_account',
      label: 'Destination Account / Entity',
      description: '🏦 Where is this transaction going?\n\nProvide account ID, vendor name, or entity identifier.\nExamples: "ACCT-9876543", "Vendor: Acme Corp", "Internal: Marketing"',
      fieldName: 'destination',
      fieldType: 'text',
      required: true,
      placeholder: 'ACCT-9876543 or Vendor: Acme Corp',
      validation: (value) => {
        if (!value?.trim()) return 'Destination is required';
        if (value.length > 200) return 'Max 200 characters';
        return null;
      }
    },
    {
      id: 'control_memo',
      label: 'Control Test Memo',
      description: '📋 Explanation for auditors\n\n**Include:**\n• Business purpose\n• Control objective (what you are proving)\n• Expected outcome (within tolerance, exception, etc.)\n• SOX reference (if applicable)\n\n**Minimum 40 characters for audit trail.**',
      fieldName: 'controlMemo',
      fieldType: 'textarea',
      required: true,
      validation: (value) => {
        if (!value?.trim()) return 'Control memo is required';
        if (value.length < 40) return 'Minimum 40 characters for audit record';
        if (value.length > 3000) return 'Max 3000 characters';
        return null;
      }
    }
  ]
};

const aiSafetyGuardrailFlow: FlowPreset = {
  id: 'ai_safety_guardrail',
  name: 'AI/LLM Safety & Policy Enforcement',
  description: 'Submit AI agent action or LLM request for policy enforcement. Verifies alignment with safety constraints and generates cryptographic proof of decision.',
  industry: 'ai_governance',
  category: 'safety',
  requiresAuthority: true,
  auditClass: 'ai_safety',
  steps: [
    {
      id: 'action_intent',
      label: 'Agent Action Intent',
      description: '🤖 What is the AI agent trying to do?\n\nExamples:\n• **Tool call**: Execute external function (API, database, etc.)\n• **Data mutation**: Modify state or records\n• **Payment**: Initiate financial transaction\n• **Message send**: Communicate externally\n• **Deploy**: Change production systems',
      fieldName: 'intentType',
      fieldType: 'select',
      options: [
        'tool_call',
        'data_mutation',
        'payment_initiation',
        'external_message',
        'deployment_action',
        'api_call'
      ],
      required: true,
      validation: (value) => value ? null : 'Action intent is required'
    },
    {
      id: 'target_system',
      label: 'Target System / API',
      description: '🎯 Which system will be affected?\n\nProvide the specific service, API, or tool the agent wants to call.\nExamples: "Stripe API", "PostgreSQL DB", "SendGrid", "AWS Lambda"',
      fieldName: 'targetSystem',
      fieldType: 'text',
      required: true,
      placeholder: 'Stripe API, PostgreSQL, SendGrid, etc.',
      validation: (value) => {
        if (!value?.trim()) return 'Target system is required';
        if (value.length > 200) return 'Max 200 characters';
        return null;
      }
    },
    {
      id: 'action_parameters',
      label: 'Structured Parameters (JSON)',
      description: '⚙️ Provide the exact parameters the agent wants to pass\n\n**Format: Valid JSON**\n\nExamples:\n```json\n{"charge_amount": 5000, "currency": "USD", "customer_id": "cus_123"}\n{"query": "SELECT * FROM users WHERE active=true", "limit": 100}\n```\n\n**This is what will be reviewed for policy compliance.**',
      fieldName: 'parameters',
      fieldType: 'json',
      required: true,
      placeholder: '{"param": "value"}',
      validation: (value) => {
        if (!value?.trim()) return 'Parameters are required';
        try { 
          JSON.parse(value); 
          return null; 
        } catch { 
          return 'Invalid JSON format'; 
        }
      }
    },
    {
      id: 'safety_justification',
      label: 'Safety Justification',
      description: '🛡️ Why is this action safe and aligned with policy?\n\n**Include:**\n• Business purpose\n• Risk assessment (low/medium/high)\n• Alignment check (how does this match policy constraints?)\n• Fallback plan (what happens if denied?)\n\n**Minimum 30 characters for safety record.**',
      fieldName: 'safetyJustification',
      fieldType: 'textarea',
      required: true,
      validation: (value) => {
        if (!value?.trim()) return 'Safety justification is required';
        if (value.length < 30) return 'Minimum 30 characters for safety record';
        if (value.length > 2000) return 'Max 2000 characters';
        return null;
      }
    },
    {
      id: 'expected_decision',
      label: 'Expected Policy Decision',
      description: '⚖️ What outcome are you testing?\n\n• **Safe/Permitted**: This should be allowed (positive test)\n• **Unsafe/Blocked**: This should be denied (negative test)\n• **Baseline match**: Behavior unchanged from last approved version',
      fieldName: 'expectedDecision',
      fieldType: 'select',
      options: ['safe_permitted', 'unsafe_blocked', 'baseline_match'],
      required: true,
      validation: (value) => value ? null : 'Expected decision is required'
    }
  ]
};

const infrastructureSLOGateFlow: FlowPreset = {
  id: 'infrastructure_slo_gate',
  name: 'Infrastructure SLO & Release Gate',
  description: 'Submit infrastructure change or release for SLO verification. Gates deployments based on performance thresholds and baseline regression checks.',
  industry: 'infrastructure',
  category: 'release_gating',
  requiresAuthority: true,
  auditClass: 'deployment_gate',
  steps: [
    {
      id: 'gate_type',
      label: 'Release Gate Type',
      description: '🚀 What type of gate is this?\n\nExamples:\n• **Performance gate**: Latency, throughput within SLO\n• **Security gate**: Vulnerability scan, access control check\n• **Regression gate**: No behavior change from baseline\n• **Canary check**: Limited rollout validation',
      fieldName: 'gateType',
      fieldType: 'select',
      options: [
        'performance_slo',
        'security_scan',
        'regression_check',
        'canary_validation',
        'config_verification'
      ],
      required: true,
      validation: (value) => value ? null : 'Gate type is required'
    },
    {
      id: 'slo_metric',
      label: 'SLO Metric / Baseline',
      description: '📈 What metric or baseline are you testing?\n\nExamples:\n• "p95 latency < 100ms"\n• "Error rate < 0.1%"\n• "No regressions from baseline v1.2.3"\n• "Zero critical CVEs"\n\n**Be specific. This defines your pass/fail criteria.**',
      fieldName: 'sloMetric',
      fieldType: 'text',
      required: true,
      placeholder: 'p95 latency < 100ms',
      validation: (value) => {
        if (!value?.trim()) return 'SLO metric is required';
        if (value.length < 10) return 'Min 10 characters';
        if (value.length > 300) return 'Max 300 characters';
        return null;
      }
    },
    {
      id: 'deployment_target',
      label: 'Deployment Target / Environment',
      description: '🎯 Where is this change going?\n\nExamples: "Production (us-east-1)", "Staging", "Canary (5%)", "All regions"',
      fieldName: 'destination',
      fieldType: 'text',
      required: true,
      placeholder: 'Production (us-east-1)',
      validation: (value) => {
        if (!value?.trim()) return 'Deployment target is required';
        if (value.length > 200) return 'Max 200 characters';
        return null;
      }
    },
    {
      id: 'change_description',
      label: 'Change Description',
      description: '📝 What is being deployed or changed?\n\n**Include:**\n• Service/component name\n• Version or commit SHA\n• Brief description of change\n• Rollback plan (if applicable)\n\n**Minimum 30 characters for deployment record.**',
      fieldName: 'changeDescription',
      fieldType: 'textarea',
      required: true,
      validation: (value) => {
        if (!value?.trim()) return 'Change description is required';
        if (value.length < 30) return 'Minimum 30 characters for deployment record';
        if (value.length > 2000) return 'Max 2000 characters';
        return null;
      }
    },
    {
      id: 'expected_outcome',
      label: 'Expected Gate Outcome',
      description: '✅ Should this pass or fail the gate?\n\n• **Deploy/Pass**: Change meets SLO (positive test)\n• **Block/Fail**: Change violates SLO (negative test - testing gate works)\n• **Baseline match**: No regression from previous version',
      fieldName: 'expectedOutcome',
      fieldType: 'select',
      options: ['deploy_pass', 'block_fail', 'baseline_match'],
      required: true,
      validation: (value) => value ? null : 'Expected outcome is required'
    }
  ]
};

export const FLOW_PRESETS: Record<string, FlowPreset> = {
  legal_evidence_filing: legalEvidenceFilingFlow,
  finance_control_attestation: financeControlAttestationFlow,
  ai_safety_guardrail: aiSafetyGuardrailFlow,
  infrastructure_slo_gate: infrastructureSLOGateFlow
};

export function getFlowPreset(id: string): FlowPreset | null {
  return FLOW_PRESETS[id] || null;
}

export function getAllFlowPresets(): FlowPreset[] {
  return Object.values(FLOW_PRESETS);
}

export function getFlowsByIndustry(industry: string): FlowPreset[] {
  return Object.values(FLOW_PRESETS).filter(flow => flow.industry === industry);
}
